import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { updateUserPreferences, getUserPreferences } from '@/lib/supabase/user';
import { Loader2 } from 'lucide-react';
import { captureEvent, EVENTS, setUserProperties } from '@/lib/posthog';
import { updateGleapContact } from '@/lib/gleap';

const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
] as const;

const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '30/01/2026' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '01/30/2026' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2026-01-30' },
] as const;

const CURRENCIES = [
  { value: 'GBP', label: 'British Pound (£)' },
  { value: 'USD', label: 'US Dollar ($)' },
  { value: 'EUR', label: 'Euro (€)' },
  { value: 'AED', label: 'UAE Dirham (د.إ)' },
  { value: 'AUD', label: 'Australian Dollar (A$)' },
  { value: 'BRL', label: 'Brazilian Real (R$)' },
  { value: 'CAD', label: 'Canadian Dollar (C$)' },
  { value: 'CHF', label: 'Swiss Franc (Fr)' },
  { value: 'CNY', label: 'Chinese Yuan (¥)' },
  { value: 'HKD', label: 'Hong Kong Dollar (HK$)' },
  { value: 'IDR', label: 'Indonesian Rupiah (Rp)' },
  { value: 'INR', label: 'Indian Rupee (₹)' },
  { value: 'JPY', label: 'Japanese Yen (¥)' },
  { value: 'KRW', label: 'South Korean Won (₩)' },
  { value: 'MXN', label: 'Mexican Peso (MX$)' },
  { value: 'MYR', label: 'Malaysian Ringgit (RM)' },
  { value: 'NOK', label: 'Norwegian Krone (kr)' },
  { value: 'NZD', label: 'New Zealand Dollar (NZ$)' },
  { value: 'PHP', label: 'Philippine Peso (₱)' },
  { value: 'PLN', label: 'Polish Złoty (zł)' },
  { value: 'QAR', label: 'Qatari Riyal (ر.ق)' },
  { value: 'SAR', label: 'Saudi Riyal (ر.س)' },
  { value: 'SEK', label: 'Swedish Krona (kr)' },
  { value: 'SGD', label: 'Singapore Dollar (S$)' },
  { value: 'THB', label: 'Thai Baht (฿)' },
  { value: 'TRY', label: 'Turkish Lira (₺)' },
  { value: 'VND', label: 'Vietnamese Dong (₫)' },
  { value: 'ZAR', label: 'South African Rand (R)' },
] as const;

interface Preferences {
  timezone: string;
  dateFormat: string;
  currency: string;
}

const DEFAULT_PREFERENCES: Preferences = {
  timezone: 'UTC',
  dateFormat: 'DD/MM/YYYY',
  currency: 'GBP',
};

export function PreferenceSettings() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPrefs, setIsLoadingPrefs] = useState(true);
  const [preferences, setPreferences] = useState<Preferences>(DEFAULT_PREFERENCES);
  const [savedPreferences, setSavedPreferences] = useState<Preferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    async function loadPreferences() {
      try {
        const prefs = await getUserPreferences();
        if (prefs) {
          const loadedPrefs: Preferences = {
            timezone: prefs.timezone || DEFAULT_PREFERENCES.timezone,
            dateFormat: prefs.dateFormat || DEFAULT_PREFERENCES.dateFormat,
            currency: prefs.currency || DEFAULT_PREFERENCES.currency,
          };
          setPreferences(loadedPrefs);
          setSavedPreferences(loadedPrefs);
        }
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to load preferences',
          variant: 'destructive',
        });
      } finally {
        setIsLoadingPrefs(false);
      }
    }
    loadPreferences();
  }, [toast]);

  const isDirty =
    preferences.timezone !== savedPreferences.timezone ||
    preferences.dateFormat !== savedPreferences.dateFormat ||
    preferences.currency !== savedPreferences.currency;

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await updateUserPreferences(preferences);
      setSavedPreferences(preferences);

      // Track settings update and update user properties
      captureEvent(EVENTS.SETTINGS_UPDATED, {
        timezone: preferences.timezone,
        date_format: preferences.dateFormat,
        currency: preferences.currency,
      });

      // Update PostHog user properties with new preferences
      setUserProperties({
        timezone: preferences.timezone,
        currency: preferences.currency,
      });
      updateGleapContact({
        timezone: preferences.timezone,
        currency: preferences.currency,
        dateFormat: preferences.dateFormat,
      });

      toast({
        title: 'Success',
        description: 'Your preferences have been updated',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update preferences',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setPreferences(savedPreferences);
  };

  if (isLoadingPrefs) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="grid gap-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="grid gap-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
          <Skeleton className="h-10 w-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Preferences</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label>Time Zone</Label>
            <Select
              value={preferences.timezone}
              onValueChange={(value) => setPreferences(prev => ({ ...prev, timezone: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Date Format</Label>
            <Select
              value={preferences.dateFormat}
              onValueChange={(value) => setPreferences(prev => ({ ...prev, dateFormat: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select date format" />
              </SelectTrigger>
              <SelectContent>
                {DATE_FORMATS.map(({ value, label, example }) => (
                  <SelectItem key={value} value={value}>
                    {label} <span className="text-muted-foreground ml-2">({example})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Currency</Label>
            <Select
              value={preferences.currency}
              onValueChange={(value) => setPreferences(prev => ({ ...prev, currency: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={isLoading || !isDirty}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Preferences
          </Button>
          {isDirty && (
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
